/**
 * Credits Widget for 12-Month Package Users
 * Displays remaining course enrollment credits
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Info } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { useAuth } from '../../contexts/AuthContext';

interface CreditsWidgetProps {
  className?: string;
}

export const CreditsWidget: React.FC<CreditsWidgetProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const credits = user?.credits || 0;
  const expiryDate = user?.subscriptionEndDate 
    ? new Date(user.subscriptionEndDate.toDate()).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : 'Unknown';
  
  // Calculate percentage for visual indicator
  const percentage = Math.min((credits / 50) * 100, 100);
  
  // Color based on credit level
  const getColorClass = () => {
    if (credits >= 40) return 'text-green-600 bg-green-50 border-green-200';
    if (credits >= 20) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (credits >= 10) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };
  
  const getProgressColor = () => {
    if (credits >= 40) return 'bg-green-500';
    if (credits >= 20) return 'bg-yellow-500';
    if (credits >= 10) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <Card className={className}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-primary" />
          <h3 className="font-montserrat font-semibold text-lg text-neutral-800">
            Course Credits
          </h3>
        </div>
      </div>

      {/* Credit Count */}
      <div className="mb-4">
        <div className="flex items-baseline justify-center gap-2 mb-2">
          <span className="font-montserrat font-bold text-5xl text-neutral-800">
            {credits}
          </span>
          <span className="text-neutral-500 text-lg">/ 50</span>
        </div>
        <p className="text-center text-sm text-neutral-600">
          Credits Remaining
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full ${getProgressColor()} transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Status Message */}
      <div className={`rounded-lg p-3 mb-4 border ${getColorClass()}`}>
        {credits > 40 && (
          <p className="text-sm font-medium">
            ✨ You have plenty of credits to explore courses!
          </p>
        )}
        {credits <= 40 && credits > 20 && (
          <p className="text-sm font-medium">
            👍 You have {credits} credits remaining
          </p>
        )}
        {credits <= 20 && credits > 10 && (
          <p className="text-sm font-medium">
            ⚠️ Running low on credits ({credits} remaining)
          </p>
        )}
        {credits <= 10 && credits > 0 && (
          <p className="text-sm font-medium">
            🚨 Only {credits} credits left - consider extending your subscription
          </p>
        )}
        {credits === 0 && (
          <p className="text-sm font-medium">
            ❌ No credits remaining - extend your subscription to get 50 more
          </p>
        )}
      </div>

      {/* Info */}
      <div className="bg-neutral-50 rounded-lg p-3 mb-4">
        <div className="flex gap-2">
          <Info className="w-4 h-4 text-neutral-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-neutral-600 space-y-1">
            <p>
              <strong>How credits work:</strong> Use 1 credit to start any premium course. Free courses don't require credits.
            </p>
            <p className="text-neutral-500">
              Credits expire on <strong>{expiryDate}</strong> with your subscription.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <Button
          onClick={() => navigate('/courses')}
          className="w-full"
          variant="primary"
        >
          Browse Courses
        </Button>
        
        {credits <= 20 && (
          <Button
            onClick={() => navigate('/billing')}
            className="w-full"
            variant="outline"
          >
            Extend Subscription
          </Button>
        )}
      </div>
    </Card>
  );
};
